/**
 * FeedbackPage — every user can file a bug report. Page + Section
 * dropdowns are scoped by role / feature toggle. Admins see a
 * shortcut icon in the header → /admin/feedback.
 *
 * Route: /feedback
 */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Inbox, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { usePageHeader } from '../context/PageHeaderContext';
import { getFeedbackPagesForUser } from '../constants/feedbackPages';

const FeedbackPage = () => {
  const navigate = useNavigate();
  const { api, user, isFeatureEnabled } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Same hasAdmin shape used in Layout — admin gets every toggle,
  // special users only the toggles they were granted.
  const hasAdmin = (toggle) =>
    user?.role === 'admin' || !!(user?.feature_toggles?.[toggle]);

  const pages = useMemo(
    () => getFeedbackPagesForUser({ user, isFeatureEnabled, hasAdmin }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user]
  );

  const [pageId, setPageId] = useState('');
  const [section, setSection] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const selectedPage = pages.find((p) => p.id === pageId);

  usePageHeader({
    title: 'Feedback',
    rightSlot: isAdmin ? (
      <button
        onClick={() => navigate('/admin/feedback')}
        className="w-10 h-10 rounded-full hover:bg-gray-100 dark:hover:bg-[#2a2a2a] flex items-center justify-center"
        aria-label="View all reports"
        title="View all reports"
        data-testid="header-feedback-reports-btn"
      >
        <Inbox className="w-5 h-5" />
      </button>
    ) : null,
  }, [isAdmin]);

  const submit = async () => {
    if (!selectedPage) { toast.error('Select the page where the bug is.'); return; }
    if (!section) { toast.error('Select the feature/section.'); return; }
    if (description.trim().length < 5) { toast.error('Describe the issue in a bit more detail (5+ characters).'); return; }
    setSubmitting(true);
    try {
      await api.post('/feedback', {
        page: selectedPage.label,
        section,
        description: description.trim(),
      });
      toast.success('Feedback submitted — thank you!');
      setPageId(''); setSection(''); setDescription('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to submit. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 pb-32" data-testid="feedback-page">
      <Card className="border-0 shadow-lg bg-white/90 dark:bg-[#141414] mb-4">
        <CardContent className="p-5 space-y-5">
          <div>
            <h2 className="text-xl font-bold mb-1">Report a bug</h2>
            <p className="text-xs text-muted-foreground">Help us fix issues faster — your name and role will be attached so we can follow up.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="fb-page">Page <span className="text-red-500">*</span></Label>
            <Select value={pageId} onValueChange={(v) => { setPageId(v); setSection(''); }}>
              <SelectTrigger id="fb-page" data-testid="feedback-page-select">
                <SelectValue placeholder="Where is the bug?" />
              </SelectTrigger>
              <SelectContent className="max-h-[60vh]">
                {pages.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`feedback-page-option-${p.id}`}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="fb-section">Feature / Section <span className="text-red-500">*</span></Label>
            <Select value={section} onValueChange={setSection} disabled={!selectedPage}>
              <SelectTrigger id="fb-section" data-testid="feedback-section-select">
                <SelectValue placeholder={selectedPage ? 'Which part of the page?' : 'Pick a page first…'} />
              </SelectTrigger>
              <SelectContent className="max-h-[60vh]">
                {(selectedPage?.sections || []).map((s) => (
                  <SelectItem key={s} value={s} data-testid={`feedback-section-option-${s.replace(/\s+/g,'-').toLowerCase()}`}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="fb-desc">Describe the issue <span className="text-red-500">*</span></Label>
            <Textarea
              id="fb-desc"
              rows={12}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What did you do? What happened? What did you expect? Steps to reproduce help us a lot."
              className="min-h-[260px] md:min-h-[320px] resize-y"
              data-testid="feedback-description"
            />
          </div>
        </CardContent>
      </Card>

      {/* Sticky submit bar — pinned just above the mobile bottom-nav
          (5rem) plus iOS home-indicator safe-area, with desktop
          fallback. Stays in view at all scroll positions. */}
      <div
        className="fixed left-0 right-0 z-40 bg-gradient-to-t from-background via-background/95 to-transparent pt-3"
        style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 4rem)' }}
        data-testid="feedback-submit-bar"
      >
        <div className="max-w-2xl mx-auto px-4 pb-3">
          <Button
            onClick={submit}
            disabled={submitting}
            className="w-full bg-[#586D52] hover:bg-[#4a5d46] text-white shadow-lg"
            data-testid="feedback-submit-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" />Submit Feedback</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FeedbackPage;
